import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "..";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { getSearchQuery } from "../common/helpers";
import { IQueryParams } from "../common/common.types";
import { IServiceInvoice } from "./service-invoice.types";
import { isArray } from "lodash";
import { getUniqueId } from "../../helpers";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";

//=============================== Service Invoice List ===============================
export const FETCH_SERVICE_INVOICE_LIST_PROGRESS =
  "FETCH_SERVICE_INVOICE_LIST_PROGRESS";
export const FETCH_SERVICE_INVOICE_LIST_SUCCESS =
  "FETCH_SERVICE_INVOICE_LIST_SUCCESS";
export const FETCH_SERVICE_INVOICE_LIST_FAILED =
  "FETCH_SERVICE_INVOICE_LIST_FAILED";

export const fetchServiceInvoiceListProgress = () =>
  action(FETCH_SERVICE_INVOICE_LIST_PROGRESS);
export const fetchServiceInvoiceListSuccess = (
  list: IServiceInvoice[],
  totalRecords: number,
) => action(FETCH_SERVICE_INVOICE_LIST_SUCCESS, { list, totalRecords });
export const fetchServiceInvoiceListFailed = () =>
  action(FETCH_SERVICE_INVOICE_LIST_FAILED);

export const fetchServiceInvoiceListAsync =
  (
    queryParams: IQueryParams,
    module: "GENERAL" | "APPROVAL",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = getSearchQuery(queryParams);
      dispatch(fetchServiceInvoiceListProgress());
      let url = `/service/get-service-invoice${searchQuery}`;
      if (module === "APPROVAL") {
        url = `/dynamicApproval/get-approval${searchQuery}&table_name=latest_service_invoice`;
      }
      const res = await api.get(url);
      const data: IServiceInvoice[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchServiceInvoiceListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchServiceInvoiceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Unknown Error Occurred",
          displayAs: "snackbar",
        }),
      );
    }
  };

//=============================== Fetch Single Service Invoice ===============================
export const FETCH_SINGLE_SERVICE_INVOICE_PROGRESS =
  "FETCH_SINGLE_SERVICE_INVOICE_PROGRESS";
export const FETCH_SINGLE_SERVICE_INVOICE_SUCCESS =
  "FETCH_SINGLE_SERVICE_INVOICE_SUCCESS";
export const FETCH_SINGLE_SERVICE_INVOICE_FAILED =
  "FETCH_SINGLE_SERVICE_INVOICE_FAILED";

export const fetchSingleServiceInvoiceProgress = () =>
  action(FETCH_SINGLE_SERVICE_INVOICE_PROGRESS);
export const fetchSingleServiceInvoiceSuccess = (data: IServiceInvoice) =>
  action(FETCH_SINGLE_SERVICE_INVOICE_SUCCESS, { data });
export const fetchSingleServiceInvoiceFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_SERVICE_INVOICE_FAILED, { errorMessage });

export const fetchSingleServiceInvoiceAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleServiceInvoiceProgress());
      let url = `/service/get-service-invoice?service_invoice_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data = res.data.data;
      if (data.length > 0) {
        let invoiceData = data[0];
        invoiceData.attachments = isArray(invoiceData.attachments)
          ? invoiceData.attachments
              .filter((item: any) => typeof item === "string")
              .map((file: string) => {
                return {
                  key: getUniqueId(),
                  type: "SERVER",
                  file: null,
                  name: getFileNameFromUrl(file || ""),
                  path: file,
                } as IFileUpload;
              })
          : [];
        dispatch(fetchSingleServiceInvoiceSuccess(data[0]));
      } else {
        dispatch(
          fetchSingleServiceInvoiceFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchSingleServiceInvoiceFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

//=============================== Upsert Single Service Invoice ===============================
export const upsertSingleServiceInvoiceAsync =
  (
    payload: IServiceInvoice,
    onCallback: (isSuccess: boolean, invoiceUUID?: string) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const {
        rowId,
        create_ts,
        insert_ts,
        combined_service_invoice_no,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        requested_by_uuid,
        is_user_approver,
        prefix,
        financial_year,
        dynamic_approval_uuid,
        ...restPayload
      } = payload;

      const asPayload = {
        title: payload.service_invoice_date,
      };

      // Handle file uploads
      const fileToUpload = isArray(restPayload.attachments)
        ? restPayload.attachments.filter((item) => item.type === "LOCAL")
        : [];

      if (fileToUpload.length > 0) {
        const { paths } = await uploadMultipleFile(
          fileToUpload,
          "SERVICE_INVOICE",
          asPayload,
        );
        restPayload.attachments = restPayload.attachments
          ?.filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!)
          .concat(paths as string[]) as any;
      } else {
        restPayload.attachments = restPayload.attachments
          ?.filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!) as any;
      }

      const res = await api.post(
        "/service/upsert-service-invoice",
        restPayload,
      );
      const responseData: IServiceInvoice = res.data.data;
      onCallback(true, responseData.service_invoice_uuid as string);
      dispatch(
        showMessage({
          type: "success",
          message: "Service Invoice saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//=============================== Delete Single Service Invoice ===============================
export const deleteSingleServiceInvoiceAsync =
  (
    uuid: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.delete("/service-invoice/delete-service-invoice", {
        data: { service_invoice_uuid: uuid },
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Service Invoice deleted successfully!",
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//=============================== Clear State ===============================
export const CLEAR_SINGLE_SERVICE_INVOICE = "CLEAR_SINGLE_SERVICE_INVOICE";
export const CLEAR_SERVICE_INVOICE_STATE = "CLEAR_SERVICE_INVOICE_STATE";
export const clearServiceInvoiceSync = () =>
  action(CLEAR_SINGLE_SERVICE_INVOICE);
export const clearServiceInvoiceStateSync = () =>
  action(CLEAR_SERVICE_INVOICE_STATE);

//=============================== Approval List ===============================
export const FETCH_SERVICE_INVOICE_APPROVAL_LIST_PROGRESS =
  "FETCH_SERVICE_INVOICE_APPROVAL_LIST_PROGRESS";
export const FETCH_SERVICE_INVOICE_APPROVAL_LIST_SUCCESS =
  "FETCH_SERVICE_INVOICE_APPROVAL_LIST_SUCCESS";
export const FETCH_SERVICE_INVOICE_APPROVAL_LIST_FAILED =
  "FETCH_SERVICE_INVOICE_APPROVAL_LIST_FAILED";

export const fetchServiceInvoiceApprovalListProgress = () =>
  action(FETCH_SERVICE_INVOICE_APPROVAL_LIST_PROGRESS);
export const fetchServiceInvoiceApprovalListSuccess = (
  data: IServiceInvoice[],
  totalRecords: number,
) =>
  action(FETCH_SERVICE_INVOICE_APPROVAL_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchServiceInvoiceApprovalListFailed = () =>
  action(FETCH_SERVICE_INVOICE_APPROVAL_LIST_FAILED);

export const fetchServiceInvoiceApprovalListAsync =
  (
    queryParams: IQueryParams,
    table_name: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchServiceInvoiceApprovalListProgress());
      let searchQuery = getSearchQuery(queryParams);
      if (billing_company_uuid) {
        searchQuery += `&billing_company_uuid=${billing_company_uuid}`;
      }
      let finalUrl = `/dynamicApproval/get-approval${searchQuery}&table_name=${table_name}`;
      const res = await api.get(finalUrl);
      const data: IServiceInvoice[] = res.data.data;
      dispatch(
        fetchServiceInvoiceApprovalListSuccess(data, res.data.totalRecords),
      );
    } catch (err: any) {
      dispatch(fetchServiceInvoiceApprovalListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
